/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import type React from 'react';
import { useId } from 'react';

import { type CommonProps } from '../_common/types';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';
import type { TagColor, TagSize } from '../tag-base';
import { TagBase } from '../tag-base';

interface DismissibleTagProps {
  /** Categorical color for the background */
  color?: TagColor;
  /** The content of the tag */
  children?: React.ReactNode;
  /** Icon to display before the content */
  leadingVisual?: React.ReactNode;
  /** Size of the tag */
  size?: TagSize;
  /** Callback function triggered when tag should be removed. */
  onRemove: React.MouseEventHandler<HTMLButtonElement>;
}

export const DismissibleTag = ({
  size = 'medium',
  color,
  children,
  htmlAttributes,
  leadingVisual,
  onRemove,
  ...restProps
}: CommonProps<'div', DismissibleTagProps>) => {
  const contentId = useId();

  return (
    <TagBase
      color={color}
      contentId={contentId}
      htmlAttributes={htmlAttributes}
      leadingVisual={leadingVisual}
      size={size}
      trailingContent={
        <button
          type="button"
          className="ndl-remove-icon"
          data-ndl-dismissible-tag-remove
          onClick={onRemove}
          aria-describedby={contentId}
          aria-label="Remove"
        >
          <XMarkIconOutline />
        </button>
      }
      {...restProps}
    >
      {children}
    </TagBase>
  );
};
